/**
 * Operaciones → Terminales POS (`/operaciones/terminales`).
 *
 * Puerto de `Terminales` + `views/terminales/index.php`: los puestos de venta
 * que se identifican con un código y una contraseña contra la tabla
 * `posidentifiers` (la misma que lee la app de POS).
 *
 * Reglas conservadas: nombre y contraseña obligatorios al crear (la contraseña se
 * cifra con bcrypt coste 12), el tipo de terminal (`PV`, `AUTO` y `CASH`, con
 * métodos de pago y lados para los dos primeros) y el alta delegada al servicio
 * de POS, que es quien genera el código.
 *
 * Diferencias anotadas:
 * - **Nunca se muestra la contraseña ni su hash**: el panel anterior proxeaba la
 *   respuesta del servicio tal cual, con los hashes incluidos.
 * - Editar y activar/desactivar se resuelven en la tabla local, así que funcionan
 *   aunque el servicio de POS esté caído; sólo el alta lo necesita y la pantalla
 *   avisa cuando no está configurado.
 */

import {
  ArrowLeft,
  Check,
  Copy,
  Eye,
  KeyRound,
  Pencil,
  Plus,
  Power,
  ShieldAlert,
  Terminal as TerminalIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { copiarAlPortapapeles } from '@/lib/clipboard';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import {
  useCatalogosTerminales,
  useDetalleTerminal,
  useMutarTerminales,
  useTerminales,
} from '../terminales.api';
import type { Terminal, TerminalPayload } from '../terminales.types';

/** Botón que copia el código del terminal y confirma con un visto. */
function BotonCopiar({ codigo, className }: { codigo: string; className?: string }) {
  const [copiado, setCopiado] = useState(false);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('size-6 shrink-0', className)}
      aria-label={`Copiar el código ${codigo}`}
      title={copiado ? 'Copiado' : 'Copiar código'}
      onClick={async () => {
        const listo = await copiarAlPortapapeles(codigo);
        setCopiado(listo);
        setTimeout(() => setCopiado(false), 1500);
      }}
    >
      {copiado ? (
        <Check className="size-3.5 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy className="size-3.5" aria-hidden="true" />
      )}
    </Button>
  );
}

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Detalle del terminal: sus lados y sus métodos de pago (los guarda el servicio de POS). */
function DetalleTerminalDialogo({ codigo, onCerrar }: { codigo: string; onCerrar: () => void }) {
  const consulta = useDetalleTerminal(codigo);

  if (consulta.isLoading) {
    return (
      <Dialog open onClose={onCerrar} title="Detalle del terminal" size="lg">
        <Skeleton className="h-48 w-full" />
      </Dialog>
    );
  }

  if (consulta.error || !consulta.data) {
    return (
      <Dialog
        open
        onClose={onCerrar}
        title="Detalle del terminal"
        footer={
          <Button variant="ghost" size="sm" onClick={onCerrar}>
            Cerrar
          </Button>
        }
      >
        <Alert variant="error" title="No se pudo leer el detalle">
          {mensajeDe(consulta.error, 'La API no respondió.')}
        </Alert>
      </Dialog>
    );
  }

  const { terminal, metodos_pago: metodos, lados, servicio } = consulta.data;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={terminal.nombre}
      description="Lo que el terminal tiene configurado en el servicio de POS."
      size="lg"
      footer={
        <Button variant="ghost" size="sm" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {servicio.aviso ? (
          <Alert variant="warning" title="Datos del servicio de POS">
            {servicio.aviso}
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1 rounded-control border border-ink-100 p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Código
            </span>
            <span className="flex items-center gap-1">
              <span className="truncate font-mono text-xs text-ink-800">{terminal.codigo}</span>
              <BotonCopiar codigo={terminal.codigo} />
            </span>
          </div>

          <div className="flex flex-col gap-1 rounded-control border border-ink-100 p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Estado
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant={terminal.activo ? 'success' : 'neutral'}>
                {terminal.activo ? 'Activo' : 'Inactivo'}
              </Badge>
              {terminal.tipo_etiqueta ? (
                <Badge variant="outline">{terminal.tipo_etiqueta}</Badge>
              ) : null}
            </span>
          </div>

          <div className="flex flex-col gap-1 rounded-control border border-ink-100 p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Creado
            </span>
            <span className="text-xs text-ink-700">{formatDateTimeValue(terminal.creado_en)}</span>
          </div>

          <div className="flex flex-col gap-1 rounded-control border border-ink-100 p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              Última actualización
            </span>
            <span className="text-xs text-ink-700">
              {formatDateTimeValue(terminal.actualizado_en)}
            </span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2 rounded-control border border-ink-100 p-3">
            <span className="text-sm font-medium text-ink-700">Lados del terminal</span>
            {lados.length === 0 ? (
              <p className="text-xs text-ink-400">
                {servicio.disponible ? 'Sin lados asociados.' : 'No se pudieron leer.'}
              </p>
            ) : (
              <span className="flex flex-wrap gap-1.5">
                {lados.map((lado) => (
                  <Badge key={lado.id} variant="brand">
                    {lado.nombre}
                  </Badge>
                ))}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-control border border-ink-100 p-3">
            <span className="text-sm font-medium text-ink-700">Métodos de pago</span>
            {metodos.length === 0 ? (
              <p className="text-xs text-ink-400">
                {servicio.disponible
                  ? 'Sin métodos de pago (los terminales de depósitos no los usan).'
                  : 'No se pudieron leer.'}
              </p>
            ) : (
              <span className="flex flex-wrap gap-1.5">
                {metodos.map((metodo) => (
                  <Badge key={metodo.id} variant="neutral">
                    {metodo.nombre}
                  </Badge>
                ))}
              </span>
            )}
          </div>
        </div>

        <Alert variant="info">
          La contraseña del terminal se guarda cifrada y <strong>nunca se muestra</strong>.
        </Alert>
      </div>
    </Dialog>
  );
}

export function TerminalesPage() {
  useDocumentTitle('Operaciones · Terminales POS');

  const terminales = useTerminales();
  const catalogos = useCatalogosTerminales();
  const mutaciones = useMutarTerminales();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; terminal: Terminal | null }>({
    abierto: false,
    terminal: null,
  });
  const [porCambiar, setPorCambiar] = useState<Terminal | null>(null);
  const [detalle, setDetalle] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [tipo, setTipo] = useState('PV');
  const [metodos, setMetodos] = useState<number[]>([]);
  const [lados, setLados] = useState<number[]>([]);

  const sinPermiso = terminales.error instanceof ApiError && terminales.error.status === 403;
  const lista = terminales.data?.terminales ?? [];
  const paginacion = usePaginacion(lista);
  const servicioListo = terminales.data?.servicio_pos.configurado ?? false;
  const usaRecursos = tipo !== 'CASH';

  // Reglas del autoservicio: sólo IZIPAY como método de pago y un único lado.
  const esAutoservicio = tipo === 'AUTO';
  const idIzipay = catalogos.data?.autoservicio.metodo_pago ?? null;
  const ladosMax = esAutoservicio ? (catalogos.data?.autoservicio.lados_max ?? 1) : null;

  const cambiarTipo = (nuevo: string) => {
    setTipo(nuevo);

    if (nuevo === 'AUTO') {
      // Se deja IZIPAY marcado y como mucho un lado.
      setMetodos(idIzipay === null ? [] : [idIzipay]);
      setLados((actuales) => actuales.slice(0, 1));
    }
  };

  const abrir = (terminal: Terminal | null) => {
    setErrorDialogo(null);
    setNombre(terminal?.nombre ?? '');
    setPassword('');
    setTipo('PV');
    setMetodos([]);
    setLados([]);
    setDialogo({ abierto: true, terminal });
  };

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/operaciones"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Operaciones
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <TerminalIcon className="size-6 text-brand-500" aria-hidden="true" />
          Terminales POS
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Puestos de venta del sistema: cada terminal se identifica con su código y su contraseña.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {terminales.data ? (
          <Badge variant="outline">
            {terminales.data.activos} activos de {terminales.data.total}
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || !servicioListo}
          title={
            servicioListo
              ? 'Nuevo terminal'
              : 'El servicio de POS no está configurado en EDSuite API'
          }
          onClick={() => abrir(null)}
        >
          Nuevo terminal
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>users</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = () => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, terminal: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar.')),
    };

    if (dialogo.terminal) {
      const payload: TerminalPayload = { nombre: nombre.trim() };
      if (password !== '') payload.password = password;
      mutaciones.actualizar.mutate({ codigo: dialogo.terminal.codigo, payload }, alGuardar);
      return;
    }

    const payload: TerminalPayload = { nombre: nombre.trim(), password, tipo };
    if (usaRecursos) {
      payload.metodos_pago = metodos;
      payload.lados = lados;
    }
    mutaciones.crear.mutate(payload, alGuardar);
  };
  const cambiarEstado = () => {
    if (!porCambiar) return;

    mutaciones.cambiarEstado.mutate(
      { codigo: porCambiar.codigo, activo: !porCambiar.activo },
      {
        onSuccess: (datos) => {
          setPorCambiar(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => {
          setPorCambiar(null);
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') });
        },
      },
    );
  };

  const valido =
    nombre.trim() !== '' &&
    (dialogo.terminal !== null || password.length >= 6) &&
    (dialogo.terminal === null || password === '' || password.length >= 6) &&
    (!esAutoservicio || (idIzipay !== null && metodos.length === 1));

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {!servicioListo && terminales.data ? (
        <Alert variant="warning" title="Alta de terminales deshabilitada">
          El servicio de POS no está configurado en EDSuite API
          (<code>EDSUITE_POS_URL</code> y <code>EDSUITE_POS_CODE</code>), así que no se pueden crear
          terminales nuevos. El listado, la edición y el estado funcionan igual.
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Terminales registrados</CardTitle>
          <Badge variant="neutral">{terminales.data?.total ?? 0} en total</Badge>
        </CardHeader>
        <CardContent>
          {terminales.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : terminales.error ? (
            <Alert variant="error" title="No se pudieron leer los terminales">
              {mensajeDe(terminales.error, 'La API no respondió.')}
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="pb-2">Nombre</th>
                    <th className="w-72 pb-2">Código</th>
                    <th className="w-40 pb-2">Creado</th>
                    <th className="w-28 pb-2">Estado</th>
                    <th className="w-44 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginacion.visibles.map((terminal) => (
                    <tr key={terminal.codigo} className="border-t border-ink-100">
                      <td className="py-2 font-medium text-ink-900">{terminal.nombre}</td>
                      <td className="py-2 pr-3">
                        <span className="flex items-center gap-1.5 font-mono text-xs text-ink-600">
                          <KeyRound className="size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
                          <span className="truncate" title={terminal.codigo}>
                            {terminal.codigo}
                          </span>
                          <BotonCopiar codigo={terminal.codigo} />
                        </span>
                      </td>
                      <td className="py-2 text-xs text-ink-600">
                        {formatDateTimeValue(terminal.creado_en)}
                      </td>
                      <td className="py-2">
                        <Badge variant={terminal.activo ? 'success' : 'neutral'}>
                          {terminal.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Ver detalles de ${terminal.nombre}`}
                            title="Ver detalles (lados y métodos de pago)"
                            onClick={() => setDetalle(terminal.codigo)}
                          >
                            <Eye className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${terminal.nombre}`}
                            title="Editar"
                            onClick={() => abrir(terminal)}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn('text-xs', terminal.activo ? 'text-ink-500' : 'text-brand-600')}
                            leftIcon={<Power className="size-3.5" aria-hidden="true" />}
                            onClick={() => setPorCambiar(terminal)}
                          >
                            {terminal.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lista.length > 0 ? <Paginador {...paginacion} etiqueta="terminales" className="mt-3" /> : null}

          <Alert variant="info" className="mt-4">
            La contraseña del terminal se guarda cifrada y <strong>nunca se muestra</strong>. El
            código lo genera el servicio de POS al crear el terminal.
          </Alert>
        </CardContent>
      </Card>

      {dialogo.abierto ? (
        <Dialog
          open
          onClose={() => setDialogo({ abierto: false, terminal: null })}
          title={dialogo.terminal ? `Editar ${dialogo.terminal.nombre}` : 'Nuevo terminal POS'}
          description={
            dialogo.terminal
              ? 'El código del terminal no se puede cambiar. Deja la contraseña en blanco para mantenerla.'
              : 'El código lo genera el servicio de POS al crearlo.'
          }
          size="lg"
          footer={
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
                onClick={() => setDialogo({ abierto: false, terminal: null })}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                loading={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
                disabled={!valido}
                onClick={guardar}
              >
                {dialogo.terminal ? 'Guardar cambios' : 'Crear terminal'}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            {errorDialogo ? (
              <Alert variant="error" title="No se pudo guardar">{errorDialogo}</Alert>
            ) : null}

            <Input
              label="Nombre del terminal"
              value={nombre}
              maxLength={255}
              autoFocus
              placeholder="Ej. POS Punto Venta"
              onChange={(evento) => setNombre(evento.target.value)}
            />

            <Input
              label={dialogo.terminal ? 'Nueva contraseña' : 'Contraseña'}
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder={dialogo.terminal ? 'Dejar en blanco para mantenerla' : 'Mínimo 6 caracteres'}
              hint="La usa la app de POS para identificarse"
              onChange={(evento) => setPassword(evento.target.value)}
            />

            {!dialogo.terminal ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="terminal-tipo" className="text-sm font-medium text-ink-700">
                    Tipo de terminal
                  </label>
                  <Select
                    id="terminal-tipo"
                    className={CONTROL}
                    value={tipo}
                    onChange={(evento) => cambiarTipo(evento.target.value)}
                  >
                    {(catalogos.data?.tipos ?? []).map((opcion) => (
                      <option key={opcion.valor} value={opcion.valor}>
                        {opcion.etiqueta}
                      </option>
                    ))}
                  </Select>
                </div>

                {usaRecursos ? (
                  <div className="flex flex-col gap-3">
                    {esAutoservicio ? (
                      <Alert variant="info">
                        Un terminal de <strong>autoservicio</strong> sólo puede cobrar con{' '}
                        <strong>IZIPAY</strong> y asociarse a <strong>un único lado</strong>.
                      </Alert>
                    ) : null}

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="flex flex-col gap-2 rounded-control border border-ink-100 p-3">
                        <span className="text-sm font-medium text-ink-700">Métodos de pago</span>
                        <div className="max-h-40 space-y-1 overflow-y-auto">
                          {(catalogos.data?.metodos_pago ?? []).map((metodo) => {
                            const permitido = !esAutoservicio || metodo.id === idIzipay;

                            return (
                              <Checkbox
                                key={metodo.id}
                                label={metodo.nombre}
                                disabled={!permitido}
                                checked={metodos.includes(metodo.id)}
                                onChange={(evento) =>
                                  setMetodos((actuales) =>
                                    evento.target.checked
                                      ? [...actuales, metodo.id]
                                      : actuales.filter((id) => id !== metodo.id),
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                        {esAutoservicio ? (
                          <p className="text-[11px] text-ink-400">
                            Los demás métodos están deshabilitados para el autoservicio.
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-col gap-2 rounded-control border border-ink-100 p-3">
                        <span className="text-sm font-medium text-ink-700">
                          Lados
                          {ladosMax !== null ? (
                            <span className="ml-1 text-[11px] font-normal text-ink-400">
                              (máximo {ladosMax})
                            </span>
                          ) : null}
                        </span>
                        <div className="max-h-40 space-y-1 overflow-y-auto">
                          {(catalogos.data?.lados ?? []).map((lado) => (
                            <Checkbox
                              key={lado.id}
                              label={lado.nombre}
                              checked={lados.includes(lado.id)}
                              onChange={(evento) =>
                                setLados((actuales) => {
                                  if (!evento.target.checked) {
                                    return actuales.filter((id) => id !== lado.id);
                                  }

                                  // En autoservicio el lado es único: reemplaza al anterior.
                                  return ladosMax === 1 ? [lado.id] : [...actuales, lado.id];
                                })
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Alert variant="info">
                    Los terminales de <strong>depósitos (CASH)</strong> no se asocian a métodos de
                    pago ni a lados.
                  </Alert>
                )}
              </>
            ) : null}
          </div>
        </Dialog>
      ) : null}

      {detalle ? (
        <DetalleTerminalDialogo
          key={`detalle-${detalle}`}
          codigo={detalle}
          onCerrar={() => setDetalle(null)}
        />
      ) : null}

      <ConfirmDialog
        open={porCambiar !== null}
        title={porCambiar?.activo ? '¿Desactivar el terminal?' : '¿Activar el terminal?'}
        description={
          porCambiar?.activo
            ? `«${porCambiar?.nombre ?? ''}» dejará de poder identificarse contra el sistema.`
            : `«${porCambiar?.nombre ?? ''}» volverá a poder usarse.`
        }
        confirmLabel={porCambiar?.activo ? 'Sí, desactivar' : 'Sí, activar'}
        loading={mutaciones.cambiarEstado.isPending}
        onClose={() => setPorCambiar(null)}
        onConfirm={cambiarEstado}
      />
    </div>
  );
}
